import { WidgetsTypeEnum } from "src/app/enums/widgets/widgets-type.enum";
import { WidgetsContainerAbstract } from "src/app/abstract/widgets/widgets-container.abstract";
import { WidgetsAbstract } from "src/app/abstract/widgets/widgets.abstract";
import { FormModel } from "../api/form.model";
import { FormVersionModel } from "../api/form-version.model";

export class WidgetsFormModel extends WidgetsContainerAbstract {
  readonly widgetType: WidgetsTypeEnum = WidgetsTypeEnum.FORM;
  readonly badge: string = 'F';
  readonly icon: string = 'feed';
  readonly placeholderClassName: string = 'form-placeholder';
  name: string = 'Form';

  /**
   * @deprecated Better use the specific DB properties below in this class
   */
  data: FormModel = new FormModel();

  // Data in DB from Form
  dbId?: number; // Id column in DB
  customerId: string = ''; // CustomerId column
  formTypeId: number = 1;
  formType?: string; // Underwriting, Risk,...

  // Data in DB from FormVersion
  createdBy?: string;
  created: string = ''; // DB: Stored as "2022-08-15T16:35:36.120Z"
  dbParentId?: number;
  statusId: number = 1; // Draft, Approved,...
  serviceCode: string = '';

  // Rejection additinal data
  approverComments: string | undefined ;
  approverName: string | undefined;
  approverINumber: string | undefined;

  // Last updated
  lastUpdated?: Date;

  // We save label overrides at form level (top)
  labelOverrides: Map<string, string> = new Map<string, string>();

  // Data with latest version
  version?: FormVersionModel;

  children: WidgetsAbstract[] = [];

  // Transient
  isUpdate: boolean = false;

  constructor() {
    super();
    this.id += this.badge;
  }

  /**
   * Return a JSON object that represent an instance of this model class
   *
   * @note An undefined attribute won't be part of the JSON: good to save space!
   * @note For legibility, badge and widgettype at the top, arrays at the bottom
   * @note Changes in this method will have an impact in the backend API/DB. It has been modelled to contain this properties.
   * @returns JSON with form model representation
   */
  toJSON(): any {
    return {
      badge: this.badge,
      widgetType: this.widgetType,
      id: this.id,
      parentId: this.parentId ? this.parentId : undefined,
      name: this.name,
      lastUpdated: this.lastUpdated ? this.lastUpdated : undefined,

      // @deprecated Sent top level equivalent information instead
      // data: this.data === null ? undefined : this.data,

      // Data in DB from Form
      dbId: this.dbId ? this.dbId : (this.data.dbId ? this.data.dbId : undefined),
      customerId: this.customerId ? this.customerId : (this.data.customerId ? this.data.customerId : undefined),
      formTypeId: this.formTypeId ? this.formTypeId : (this.data.formTypeId ? this.data.formTypeId : undefined),

      // Data in DB from FormVersion
      createdBy: this.createdBy ? this.createdBy : (this.data.createdBy ? this.data.createdBy : undefined),
      created: this.created ? this.created : (this.data.created ? this.data.created : undefined),
      dbParentId: this.dbParentId ? this.dbParentId : (this.data.dbParentId ? this.data.dbParentId : undefined),
      statusId: this.statusId ? this.statusId : (this.data.statusId ? this.data.statusId : undefined),
      serviceCode: this.serviceCode ? this.serviceCode : (this.data.serviceCode ? this.data.serviceCode : undefined),
      approverComments: this.approverComments ? this.approverComments : undefined,
      approverName: this.approverName ? this.approverName: undefined,
      approverINumber: this.approverINumber ? this.approverINumber: undefined,

      // NOTE: Don't send version info. It's transient
      // version: this.version === null ? undefined : this.version,

      // Long attributes at the end for JSON legibility
      template: this.template ? this.template : undefined,
      labelOverrides: this.labelOverrides ? Object.fromEntries(this.labelOverrides) : undefined,
      children: this.children,
    };
  }

  /**
   * Clone this node from an origin.
   *
   * @warning Only clones current node and Not the children
   * @param origin
   * @returns the cloned node
   */
  cloneFrom(origin: WidgetsFormModel): WidgetsFormModel {
    this.name = origin.name;
    if (origin.description) {
      this.description = origin.description;
    }
    if (origin.parentId) {
      this.parentId = origin.parentId;
    }
    if (origin.lastUpdated) {
      this.lastUpdated = origin.lastUpdated;
    }

    // FormModel is cloned using the relevant method. Note FormVersions are not cloned
    this.data.cloneFrom(origin.data);

    // DB Data
    if (origin.dbId) {
      this.dbId = origin.dbId;
    }
    if (origin.customerId) {
      this.customerId = origin.customerId;
    }
    if (origin.formTypeId) {
      this.formTypeId = origin.formTypeId;
    }
    if (origin.createdBy) {
      this.createdBy = origin.createdBy;
    }
    if (origin.created) {
      this.created = origin.created;
    }
    if (origin.dbParentId) {
      this.dbParentId = origin.dbParentId;
    }
    if (origin.statusId) {
      this.statusId = origin.statusId;
    }
    if (origin.serviceCode) {
      this.serviceCode = origin.serviceCode;
    }
    // Approve/Reject comments ar part of the version
    if (origin.approverComments) {
      this.approverComments = origin.approverComments;
    }
    if (origin.approverName) {
        this.approverName = origin.approverName;
    }
    if (origin.approverINumber) {
        this.approverINumber = origin.approverINumber;
    }
    // End DB dATA

    if (origin.template) {
      this.template = origin.template;
    }
    if (origin.labelOverrides && origin.labelOverrides.size > 0) {
      this.labelOverrides = new Map<string, string>(origin.labelOverrides);
    }

    // Only last version is copied here
    this.version = Object.assign([], origin.version);
    this.isSelected = origin.isSelected;
    return this;
  }

  /**
   * Clone the data from DB stored in form to the relevant WidgetsFormModel properties
   *
   * @param form
   */
  cloneDbData(form: any) {
    if (form.Id) {
      this.dbId = form.Id;
    }
    this.name = form.Name;
    if (form.CustomerId) {
      this.customerId = form.CustomerId;
    }
    if (form.FormTypeId) {
      this.formTypeId = form.FormTypeId;
    }
  }

  /**
   * Clone the data from DB stored in form to the relevant WidgetsFormModel properties
   *
   * @param form
   */
  cloneDBFormVersion(form: FormVersionModel) {
    if (form.Createdby) {
      this.createdBy = form.Createdby;
    }
    if (form.Created) {
      this.created = form.Created;
    }
    if (form.ParentId) {
      this.dbParentId = form.ParentId;
    }
    if (form.StatusId) {
      this.statusId = form.StatusId;
    }
    if (form.ServiceCode) {
      this.serviceCode = form.ServiceCode;
    }
    if (form.Comments) {
      this.approverComments = form.Comments;
    }
    if (form.Approvedby) {
        this.approverName = form.Approvedby;
    }
    if (form.ApproverId) {
        this.approverINumber = form.ApproverId;
    }
  }
}
